import { reactive } from 'vue'
import { api, disallowRefresh, onSessionLost, onSessionRenewed, refreshSession, setAccessToken,
  type Org, type Session, type User } from './api'
import { queryClient } from './query'
import { setLocale, storedLocale } from './i18n'
import { setOrgOffsets } from './orgClock'
import { moduleOfPath } from './modules'

/**
 * The session: user, org, and whether the first check is done. A page load
 * resumes the session from the refresh cookie: without it, a reload would
 * send an agent back to the login screen in the middle of a call.
 */
export interface SupportMark { by: string; until: string | null }

export const session = reactive<{
  user: User | null
  org: Org | null
  ready: boolean
  /** A support session opened from the platform console: the platform admin acting as this account. */
  support: SupportMark | null
}>({ user: null, org: null, ready: false, support: null })

/** Every session goes through here: it also sets the screens' clock to the org's. */
function applyProfile(s: Pick<Session, 'user' | 'org'>): void {
  session.user = s.user
  session.org = s.org
  setOrgOffsets(s.org?.utcOffsets)
}

function open(s: Session): Promise<void> {
  setAccessToken(s.accessToken)
  applyProfile(s)
  // The workstation's choice wins; otherwise the org's language. A language
  // that fails to load must not fail the session: the screen stays in French.
  return setLocale(storedLocale() ?? s.org.locale).catch(() => {})
}

function close(): void {
  setAccessToken(null)
  session.user = null
  session.org = null
  session.support = null
  setOrgOffsets(null)
  queryClient.clear()
}

onSessionLost(close)

/*
 * A renewed session for another account or org (a login in another tab of
 * this browser) ends this one: applying it would show one account's screen
 * with another's rights and cached data. The same account gets its profile
 * refreshed (a role or an extension may have changed).
 */
onSessionRenewed((s) => {
  if (!session.user) return true
  if (s.user.id !== session.user.id || s.org.id !== session.org?.id) {
    close()
    return false
  }
  applyProfile(s)
  return true
})

/**
 * A page that needs no org session: a TV screen with its own token, the
 * platform console (its own session, above every org) and an invitation
 * link. No org session is resumed there, so nothing of an org (its clock,
 * its language) leaks into them.
 */
const sessionless = (): boolean =>
  ((location.pathname === '/wall' || location.pathname === '/mur') && new URLSearchParams(location.hash.slice(1)).has('t'))
  || /^\/(platform|plateforme)(\/|$)/.test(location.pathname) || location.pathname === '/invitation'

/**
 * A support session opened from the platform console: its token comes in the
 * address, once (`/support#t=…&fin=…`). It is never renewed.
 */
async function startSupport(): Promise<boolean> {
  const params = location.pathname === '/support' ? new URLSearchParams(location.hash.slice(1)) : null
  const token = params?.get('t')
  if (!token) return false
  disallowRefresh()
  setAccessToken(token)
  history.replaceState(history.state, '', '/')
  try {
    const me = await api<{ user: User; org: Org; support: { by: string } | null }>('/auth/me')
    await open({ accessToken: token, user: me.user, org: me.org })
    session.support = me.support ? { by: me.support.by, until: params!.get('fin') } : null
  } catch {
    setAccessToken(null)
  }
  return true
}

let started: Promise<void> | null = null
export function startSession(): Promise<void> {
  started ??= (async () => {
    if (await startSupport()) return
    if (sessionless()) {
      // A TV screen never renews anything; the console and an invitation lead to a full page load elsewhere.
      if (location.pathname === '/wall' || location.pathname === '/mur') disallowRefresh()
      return
    }
    // A server that cannot answer is not a refusal, but without a session the login shows.
    const s = await refreshSession().catch(() => null)
    if (s) await open(s)
  })()
    .then(() => (session.user ? undefined : setLocale(storedLocale() ?? 'fr').catch(() => {})))
    .finally(() => { session.ready = true })
  return started
}

/** `org` is the slug; the server ignores it when the address names the org. */
export async function login(org: string | null, username: string, password: string): Promise<void> {
  await open(await api<Session>('/auth/login', {
    method: 'POST', body: { username, password, ...(org ? { org } : {}) },
  }))
}

const beforeLogout = new Set<() => void | Promise<void>>()
/**
 * Work to finish before the session ends (the agent session, the softphone,
 * open streams). Returns the unsubscribe.
 */
export function onLogout(fn: () => void | Promise<void>): () => void {
  beforeLogout.add(fn)
  return () => { beforeLogout.delete(fn) }
}

export async function logout(): Promise<void> {
  for (const fn of [...beforeLogout]) {
    try { await fn() } catch { /* never block a logout */ }
  }
  try { await api('/auth/logout', { method: 'POST' }) } catch { /* already invalid */ }
  close()
}

/** Admins hold every permission. */
export const can = (permission: string): boolean =>
  Boolean(session.user && (session.user.role === 'admin' || session.user.permissions.includes(permission)))

/** Whether the org has a module switched on (an org profile without the list has them all). */
export const hasModule = (module: string): boolean => !session.org?.modules || session.org.modules.includes(module)

/** Whether a screen path is open to the org: its module switched on, or a core screen. */
export function screenOn(path: string): boolean {
  const m = moduleOfPath(path)
  return !m || hasModule(m)
}
